import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { getArtworkIdBySide } from "@design-lab/shared/lib/artworkUtils";
import type { ArtworkForm, Side } from "@design-lab/shared/types";
import { useArtworkEnhancedImages } from "./useArtworkEnhancedImages";
import { useArtwork } from "./useArtwork";
import { useArtworkForm } from "./useArtworkForm";

export type UseEnhancedImageSourceProps = {
  side: Side;
};

export function useEnhancedImageSource({ side }: UseEnhancedImageSourceProps) {
  const { getArtwork } = useArtwork();
  const { form } = useArtworkForm();

  const artworkForm = useWatch({
    control: form.control
  }) as ArtworkForm | undefined;

  // Provide default values when form is not initialized
  const info = artworkForm?.info || {
    front: { removeBackground: true, enhancementTier: "basic" as const, editImageManual: false },
    back: { removeBackground: true, enhancementTier: "basic" as const, editImageManual: false }
  };

  const artworkInfoForm = info?.[side];
  const artworkIdForm = getArtworkIdBySide(artworkForm, side) || "";
  const artwork = getArtwork(artworkIdForm);

  const enhancedImages = useArtworkEnhancedImages(
    artwork,
    artworkInfoForm?.removeBackground || false,
    artwork?.canRemoveBg ?? true,
    artworkInfoForm?.editImageManual || false
  );

  const source = useMemo(() => {
    if (!artwork || !enhancedImages || !artworkForm || !artworkInfoForm)
      return {
        enhancementTier: undefined,
        enhancedImage: undefined
      };

    if (
      artworkInfoForm.enhancementTier === "print_original" ||
      artworkInfoForm.enhancementTier === "custom"
    ) {
      return {
        enhancementTier: artworkInfoForm.enhancementTier,
        enhancedImage: enhancedImages[artworkInfoForm.enhancementTier]
      };
    }

    if (artworkInfoForm.enhancementTier === "full_enhancements") {
      return {
        enhancementTier: artworkInfoForm.enhancementTier,
        enhancedImage: enhancedImages[artworkInfoForm.enhancementTier]
      };
    }

    return {};
  }, [artwork, enhancedImages, artworkForm, artworkInfoForm]);

  return {
    source,
    artworkId: artworkIdForm || ""
  };
}
